import { InjectionToken } from '@angular/core';

export interface AppConfig {
  name: string;
  apiHost: string;
  production: boolean;
}

export const APP_ENV_CONFIG = new InjectionToken<AppConfig>('Application Environment Config');

// Local dev runs against the KOR non-prod QA backend for both brands - the deployed
// SALT API does not accept localhost origins (matches portal-client).
const LOCAL_DEV_CONFIG: AppConfig = {
  name: 'qa',
  production: false,
  apiHost: 'https://api.qa.rocketkor-nonprod.net',
};

const isLocalHost = (host: string): boolean =>
  /^(salt\.)?(localhost|127\.0\.0\.1)(:\d+)?$/.test(host);

/**
 * Resolves the API environment from the host.
 *
 * Hosts are matched exactly rather than by substring: an unrecognised host throws
 * instead of falling back to QA, so a misconfigured or newly added host fails loudly
 * at startup rather than silently serving production traffic against the QA backend.
 */
export const getEnvironment = (host: string = window.location.host): AppConfig => {
  switch (host) {
    case 'securecapture.rocketkor.net':
      return { name: 'production', production: true, apiHost: 'https://api.rocketkor.net' };

    case 'securecapture.salttreasury.com':
      return { name: 'production', production: true, apiHost: 'https://api.salttreasury.com' };

    case 'securecapture.sandbox.rocketkor.net':
      return { name: 'sandbox', production: true, apiHost: 'https://api.sandbox.rocketkor.net' };

    case 'securecapture.sandbox.salttreasury.com':
      return { name: 'sandbox', production: true, apiHost: 'https://api.sandbox.salttreasury.com' };

    case 'securecapture.staging.rocketkor-nonprod.net':
      return { name: 'staging', production: false, apiHost: 'https://api.staging.rocketkor-nonprod.net' };

    case 'securecapture.staging.salttreasury.com':
      return { name: 'staging', production: false, apiHost: 'https://api.staging.salttreasury.com' };

    case 'securecapture.qa.rocketkor-nonprod.net':
      return { name: 'qa', production: false, apiHost: 'https://api.qa.rocketkor-nonprod.net' };

    case 'securecapture.qa.salttreasury.com':
      return { name: 'qa', production: false, apiHost: 'https://api.qa.salttreasury.com' };

    default:
      if (isLocalHost(host)) {
        return LOCAL_DEV_CONFIG;
      }

      throw new Error(`Unrecognised host: ${host}`);
  }
};
